import React, { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasRole } from "@/pages/admin/AdminShell";
import { LockedQuestions, QuestionsEditor } from "@/components/FieldEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

function TypeEditor({ cur, setCur, onSave }: any) {
  return (
    <div className="space-y-3 rounded-sm border bg-card p-4" data-testid="event-type-editor">
      <div className="space-y-1.5"><Label>Name</Label><Input value={cur.name} onChange={(e) => setCur({ ...cur, name: e.target.value })} data-testid="event-type-name-input" /></div>
      <div className="space-y-1.5"><Label>Description</Label><Input value={cur.description || ""} onChange={(e) => setCur({ ...cur, description: e.target.value })} data-testid="event-type-description-input" /></div>
      <label className="flex items-center justify-between rounded-sm border p-3 text-sm">Active (vendors can pick it for new events)
        <Switch checked={cur.active} onCheckedChange={(v) => setCur({ ...cur, active: v })} data-testid="event-type-active-switch" /></label>
      <h3 className="pt-2 font-semibold">Base questions</h3>
      <QuestionsEditor fields={cur.questions} onChange={(questions) => setCur({ ...cur, questions })} prefix="event-type" />
      <Button onClick={onSave} className="bg-primary text-white hover:bg-primary/90" data-testid="event-type-save-button">Save event type</Button>
    </div>
  );
}

export default function AdminEventTypes() {
  const { admin } = useAuth();
  const canEdit = hasRole(admin?.user, ["admin"]);
  const [types, setTypes] = useState<any[]>([]);
  const [cur, setCur] = useState<any>(null);
  const load = async (id?: string) => {
    const { data } = await api.get("/admin/event-types");
    setTypes(data);
    setCur(data.find((t: any) => t.id === (id || cur?.id)) || data[0] || null);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const { data } = cur.id ? await api.put(`/admin/event-types/${cur.id}`, cur) : await api.post("/admin/event-types", cur);
      toast.success("Event type saved");
      load(data.id);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const move = async (i: number, d: number) => {
    const ids = types.map((t) => t.id);
    [ids[i], ids[i + d]] = [ids[i + d], ids[i]];
    try { await api.post("/admin/event-types/reorder", { ids }); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Standard questions</p>
          <h1 className="text-3xl font-semibold tracking-tight">Event types</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">Attendees answer each type's base questions once. Their answers go to every vendor whose booth they scan at events of that type.</p>
        </div>
        {canEdit && <Button onClick={() => setCur({ name: "New event type", description: "", active: true, questions: [] })} className="bg-primary text-white" data-testid="event-type-new-button"><Plus className="mr-1 h-4 w-4" /> New event type</Button>}
      </div>
      <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
        <div className="space-y-2" data-testid="event-type-list">
          {types.map((t, i) => (
            <div key={t.id} className={`flex items-center gap-2 rounded-sm border p-3 ${cur?.id === t.id ? "border-primary bg-primary/5" : "bg-card"}`} data-testid={`event-type-row-${t.id}`}>
              <button className="min-w-0 flex-1 text-left" onClick={() => setCur(t)} data-testid={`event-type-open-${t.id}`}>
                <p className="truncate font-semibold">{t.name}</p>
                <p className="text-xs text-muted-foreground">{t.questions.length} questions{t.active ? "" : " · Inactive"}</p>
              </button>
              {canEdit && <>
                <Button size="icon" variant="ghost" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`event-type-up-${t.id}`}><ArrowUp className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" disabled={i === types.length - 1} onClick={() => move(i, 1)} data-testid={`event-type-down-${t.id}`}><ArrowDown className="h-4 w-4" /></Button>
              </>}
            </div>
          ))}
        </div>
        {cur && (canEdit ? <TypeEditor key={cur.id || "new"} cur={cur} setCur={setCur} onSave={save} /> : (
          <div className="rounded-sm border bg-card p-4" data-testid="event-type-readonly">
            <p className="mb-3 text-sm text-muted-foreground">View only. Ask an admin to change event types.</p>
            <LockedQuestions eventType={cur} />
          </div>
        ))}
      </div>
    </div>
  );
}
